import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NOTIFICATION_API_BASE } from '../config';

const MESSAGE_NOTIFICATION = 'message_received';
const LIKE_NOTIFICATION = 'like_changed';
const MUTUAL_NOTIFICATION = 'mutual_like';
const NOTIFICATIONS_SYNC_EVENT = 'tinterest:notifications-updated';

const normalizeNotification = (raw) => ({
  id: raw?.id || raw?.Id,
  type: (raw?.type || raw?.Type || '').trim(),
  payloadJson: raw?.payloadJson ?? raw?.PayloadJson,
  createdAt: raw?.createdAt || raw?.CreatedAt
});

const safeParseJson = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export default function useNotificationSummary({ pollMs = 8000 } = {}) {
  const [notifications, setNotifications] = useState([]);
  const pollingRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setNotifications([]);
      return;
    }

    try {
      const response = await fetch(`${NOTIFICATION_API_BASE}/api/notifications`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setNotifications([]);
        }
        return;
      }

      const data = await response.json();
      const mapped = Array.isArray(data) ? data.map(normalizeNotification) : [];
      setNotifications(mapped);
    } catch (error) {
      console.warn('notification summary fetch failed', error);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    pollingRef.current = window.setInterval(loadNotifications, pollMs);

    return () => {
      if (pollingRef.current) {
        window.clearInterval(pollingRef.current);
      }
    };
  }, [loadNotifications, pollMs]);

  useEffect(() => {
    const handleSync = () => {
      loadNotifications();
    };

    window.addEventListener(NOTIFICATIONS_SYNC_EVENT, handleSync);
    return () => window.removeEventListener(NOTIFICATIONS_SYNC_EVENT, handleSync);
  }, [loadNotifications]);

  const summary = useMemo(() => {
    const mutualNotificationUserIds = new Set();
    const mutualNotificationIds = [];
    const mutualNotificationIdsByUserId = new Map();
    const likeNotificationUserIds = new Set();
    const likeNotificationIdsByUserId = new Map();
    const likeNotificationIdsWithoutUser = [];
    const likeNotificationIds = [];
    const messageNotificationIds = [];

    notifications.forEach((notification) => {
      if (notification.type === MESSAGE_NOTIFICATION) {
        messageNotificationIds.push(notification.id);
        return;
      }

      if (notification.type === LIKE_NOTIFICATION) {
        likeNotificationIds.push(notification.id);
        const payload = safeParseJson(notification.payloadJson);
        const fromUserId = payload?.fromUserId;
        if (fromUserId) {
          const normalizedUserId = String(fromUserId);
          likeNotificationUserIds.add(normalizedUserId);
          const currentIds = likeNotificationIdsByUserId.get(normalizedUserId) || [];
          currentIds.push(notification.id);
          likeNotificationIdsByUserId.set(normalizedUserId, currentIds);
        } else {
          likeNotificationIdsWithoutUser.push(notification.id);
        }
        return;
      }

      if (notification.type === MUTUAL_NOTIFICATION) {
        mutualNotificationIds.push(notification.id);
        const payload = safeParseJson(notification.payloadJson);
        const fromUserId = payload?.fromUserId;
        if (fromUserId) {
          const normalizedUserId = String(fromUserId);
          mutualNotificationUserIds.add(normalizedUserId);
          const currentIds = mutualNotificationIdsByUserId.get(normalizedUserId) || [];
          currentIds.push(notification.id);
          mutualNotificationIdsByUserId.set(normalizedUserId, currentIds);
        }
      }
    });

    return {
      hasUnreadMessages: messageNotificationIds.length > 0,
      messageNotificationIds,
      hasLikeNotifications: likeNotificationIds.length > 0,
      likeNotificationIds,
      likeNotificationIdsByUserId,
      likeNotificationIdsWithoutUser,
      likeNotificationUserIds,
      likeNotificationCount: likeNotificationIds.length,
      hasMutualNotifications: mutualNotificationIds.length > 0,
      mutualNotificationIds,
      mutualNotificationIdsByUserId,
      mutualNotificationUserIds,
      hasSympathyNotifications: likeNotificationIds.length > 0 || mutualNotificationIds.length > 0
    };
  }, [notifications]);

  const markNotificationsRead = useCallback(async (ids) => {
    const uniqueIds = Array.from(new Set((ids || []).filter(Boolean)));
    if (uniqueIds.length === 0) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const results = await Promise.all(uniqueIds.map(async (id) => {
      try {
        const response = await fetch(`${NOTIFICATION_API_BASE}/api/notifications/${id}/read`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          keepalive: true
        });

        if (response.ok || response.status === 404) {
          return { id, success: true };
        }

        return { id, success: false };
      } catch {
        return { id, success: false };
      }
    }));

    const confirmedIds = results
      .filter((result) => result.success)
      .map((result) => result.id);

    const failedIds = results
      .filter((result) => !result.success)
      .map((result) => result.id);

    if (confirmedIds.length > 0) {
      setNotifications((prev) => prev.filter((item) => !confirmedIds.includes(item.id)));
      window.dispatchEvent(new Event(NOTIFICATIONS_SYNC_EVENT));
    }

    if (failedIds.length > 0) {
      await loadNotifications();
      throw new Error(`Failed to mark notifications as read: ${failedIds.join(', ')}`);
    }
  }, [loadNotifications]);

  const markSympathyNotificationsReadAll = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const response = await fetch(`${NOTIFICATION_API_BASE}/api/notifications/read-sympathies`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      keepalive: true
    });

    if (!response.ok) {
      await loadNotifications();
      throw new Error(`Failed to mark sympathy notifications as read: ${response.status}`);
    }

    setNotifications((prev) => prev.filter((item) => (
      item.type !== LIKE_NOTIFICATION && item.type !== MUTUAL_NOTIFICATION
    )));
    window.dispatchEvent(new Event(NOTIFICATIONS_SYNC_EVENT));
  }, [loadNotifications]);

  return {
    notifications,
    reloadNotifications: loadNotifications,
    markNotificationsRead,
    markSympathyNotificationsReadAll,
    ...summary
  };
}
